import type { User } from '../types/index';

// Profile frame styles based on rank
export const getRankFrameStyle = (rank: User['rank']) => {
  const frames = {
    'beginner': {
      border: '2px solid #10b981',
      borderRadius: '50%',
      padding: '3px'
    },
    'newcomer': {
      border: '2px solid #06b6d4',
      borderRadius: '50%',
      padding: '3px'
    },
    'rookie': {
      border: '2px solid #8b5cf6',
      borderRadius: '50%',
      padding: '3px'
    },
    'novice': {
      border: '2px solid #9ca3af',
      borderRadius: '50%',
      padding: '3px',
      background: 'linear-gradient(45deg, #e5e7eb, #f3f4f6)'
    },
    'cleaner': {
      border: '3px solid #8b5cf6',
      borderRadius: '50%',
      padding: '3px',
      animation: 'rotate 3s linear infinite'
    },
    'eco-warrior': {
      border: '3px solid #a855f7',
      borderRadius: '50%',
      padding: '3px',
      boxShadow: '0 0 20px rgba(168, 85, 247, 0.6)',
      animation: 'glow 2s ease-in-out infinite'
    },
    'green-champion': {
      background: 'linear-gradient(45deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6)',
      borderRadius: '50%',
      padding: '4px',
      animation: 'rainbow 3s ease-in-out infinite'
    },
    'campus-hero': {
      border: '3px solid transparent',
      borderRadius: '50%',
      padding: '4px',
      background: 'linear-gradient(45deg, #60a5fa, #c084fc) border-box',
      backgroundClip: 'padding-box, border-box',
      position: 'relative' as const,
      '&::before': {
        content: '""',
        position: 'absolute' as const,
        inset: '-3px',
        borderRadius: '50%',
        background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
        zIndex: -1
      }
    },
    'legendary': {
      border: '4px solid #fbbf24',
      borderRadius: '50%',
      padding: '4px',
      boxShadow: '0 0 30px rgba(251, 191, 36, 0.8)',
      animation: 'pulse-gold 2s ease-in-out infinite',
      position: 'relative' as const,
      '&::after': {
        content: '"👑"',
        position: 'absolute' as const,
        top: '-15px',
        right: '-5px',
        fontSize: '20px',
        animation: 'float 2s ease-in-out infinite'
      }
    }
  };
  return frames[rank] || frames.beginner;
};

// Check if user can use specific reactions
export const getAvailableReactions = (rank: User['rank']) => {
  const baseReactions = ['love', 'clap'];
  const rankReactions: Record<User['rank'], string[]> = {
    'beginner': baseReactions,
    'newcomer': [...baseReactions, 'thumbsdown'],
    'rookie': [...baseReactions, 'thumbsdown', 'fire'],
    'novice': [...baseReactions, 'thumbsdown', 'fire'],
    'cleaner': [...baseReactions, 'thumbsdown', 'fire', 'star'],
    'eco-warrior': [...baseReactions, 'thumbsdown', 'fire', 'star'],
    'green-champion': [...baseReactions, 'thumbsdown', 'fire', 'star'],
    'campus-hero': [...baseReactions, 'thumbsdown', 'fire', 'star'],
    'legendary': [...baseReactions, 'thumbsdown', 'fire', 'star', 'muscle']
  };
  return rankReactions[rank] || baseReactions;
};

// Get bio character limit based on rank
export const getBioCharLimit = (rank: User['rank']) => {
  const limits: Record<User['rank'], number> = {
    'beginner': 50,
    'newcomer': 50,
    'rookie': 100,
    'novice': 200,
    'cleaner': 200,
    'eco-warrior': 300,
    'green-champion': 300,
    'campus-hero': 500,
    'legendary': 1000
  };
  return limits[rank] || 50;
};

// Check if user can comment
export const canComment = (rank: User['rank']) => {
  // Allow all users to comment
  return true;
  // const noCommentRanks: User['rank'][] = ['beginner'];
  // return !noCommentRanks.includes(rank);
};

// Get comment style based on rank
export const getCommentStyle = (rank: User['rank']) => {
  if (rank === 'eco-warrior' || rank === 'green-champion' || rank === 'campus-hero' || rank === 'legendary') {
    return { fontWeight: '600' };
  }
  return {};
};

// Check if user has verified badge
export const hasVerifiedBadge = (rank: User['rank']) => {
  return rank === 'campus-hero' || rank === 'legendary';
};

// Check if user can pin posts
export const canPinPosts = (rank: User['rank']) => {
  const pinRanks: User['rank'][] = ['green-champion', 'campus-hero', 'legendary'];
  return pinRanks.includes(rank);
};

// Get number of posts user can pin
export const getPinLimit = (rank: User['rank']) => {
  if (rank === 'legendary') return 3;
  if (rank === 'campus-hero') return 2;
  if (rank === 'green-champion') return 1;
  return 0;
};